import type { ShipId } from '@rttv/shared';

export interface ShipNozzle {
  x: number;
  y: number;
  halfW: number;
  len: number;
}

export interface ShipVisualDefinition {
  /** Standalone Phaser texture key for the rendered hull. */
  textureKey: string;
  /** Public URL shared by the lobby and the Phaser loader. */
  assetUrl: string;
  /** Atlas frame retained as an escape hatch for legacy textures. */
  frame?: string;
  scale: number;
  /** Optional horizontal broadening for very narrow silhouettes. */
  widthScale?: number;
  minimapScale: number;
  nozzles: readonly ShipNozzle[];
  thrusterSound: 'snd_thruster_small' | 'snd_thruster_circular' | 'snd_thruster_large';
  primarySound: 'snd_fighter_primary' | 'snd_tank_primary' | 'sfx_ufo_primary';
  noseFx?: 'ufo' | 'wraith';
}

/**
 * Every client-only ship choice lives here so a new hull cannot accidentally
 * inherit the Fighter's atlas, exhaust, minimap icon, or audio.
 */
export const SHIP_VISUALS: Record<ShipId, ShipVisualDefinition> = {
  fighter: {
    textureKey: 'ritv_fighter',
    assetUrl: '/assets/ritv/v1/ships/fighter.png',
    scale: 0.34,
    minimapScale: 0.052,
    nozzles: [{ x: 0, y: 29, halfW: 6, len: 22 }],
    thrusterSound: 'snd_thruster_small',
    primarySound: 'snd_fighter_primary',
  },
  robotai: {
    textureKey: 'ritv_robotai',
    assetUrl: '/assets/ritv/v1/ships/robotai.png',
    scale: 0.31,
    minimapScale: 0.046,
    nozzles: [],
    thrusterSound: 'snd_thruster_circular',
    primarySound: 'sfx_ufo_primary',
    noseFx: 'ufo',
  },
  juggernaut: {
    textureKey: 'ritv_juggernaut',
    assetUrl: '/assets/ritv/v1/ships/juggernaut.png',
    scale: 0.36,
    minimapScale: 0.064,
    nozzles: [
      { x: -24, y: 26, halfW: 6, len: 20 },
      { x: 24, y: 26, halfW: 6, len: 20 },
    ],
    thrusterSound: 'snd_thruster_large',
    primarySound: 'snd_tank_primary',
  },
  wraith: {
    textureKey: 'ritv_wraith',
    assetUrl: '/assets/ritv/v1/ships/wraith.png',
    scale: 0.30,
    widthScale: 1.22,
    minimapScale: 0.046,
    nozzles: [{ x: 0, y: 27, halfW: 4, len: 20 }],
    thrusterSound: 'snd_thruster_small',
    primarySound: 'snd_fighter_primary',
    noseFx: 'wraith',
  },
  corsair: {
    textureKey: 'ritv_corsair',
    assetUrl: '/assets/ritv/v1/ships/corsair.png',
    scale: 0.36,
    minimapScale: 0.052,
    nozzles: [
      { x: -9, y: 27, halfW: 4, len: 19 },
      { x: 10, y: 27, halfW: 4, len: 19 },
    ],
    thrusterSound: 'snd_thruster_large',
    primarySound: 'snd_tank_primary',
  },
  lancer: {
    textureKey: 'ritv_lancer',
    assetUrl: '/assets/ritv/v1/ships/lancer.png',
    scale: 0.30,
    widthScale: 1.28,
    minimapScale: 0.044,
    nozzles: [{ x: 0, y: 31, halfW: 5, len: 22 }],
    thrusterSound: 'snd_thruster_small',
    primarySound: 'sfx_ufo_primary',
  },
  skimmer: {
    textureKey: 'ritv_skimmer',
    assetUrl: '/assets/ritv/v1/ships/skimmer.png',
    scale: 0.34,
    minimapScale: 0.050,
    nozzles: [
      { x: -5, y: 20, halfW: 3, len: 19 },
      { x:  5, y: 20, halfW: 3, len: 19 },
    ],
    thrusterSound: 'snd_thruster_circular',
    primarySound: 'snd_fighter_primary',
  },
};

export function getShipVisual(shipId: string): ShipVisualDefinition {
  return SHIP_VISUALS[shipId as ShipId] ?? SHIP_VISUALS.fighter;
}
